import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function BookingFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertyType, setPropertyType] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(1);
  const [pin, setPin] = useState("");
  const [reserveLoading, setReserveLoading] = useState(false);
  const [reserveError, setReserveError] = useState("");
  const [reserveSuccess, setReserveSuccess] = useState("");
  // Estados para criar hospedagem
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    propertyName: "",
    propertyType: "hotel",
    description: "",
    address: "",
    city: "",
    country: "",
    pricePerNight: "",
    amenities: [],
    amenityInput: "",
    checkIn: "14:00",
    checkOut: "12:00",
    privacy: "public",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");

  const DAY_MS = 1000 * 60 * 60 * 24;

  const handleEditPost = (post) => {
    setSelectedProperty(null);
    setEditingPost(post);
  };
  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    setSelectedProperty(null);
  };

  useEffect(() => {
    if (selectedProperty) {
      setCheckIn("");
      setCheckOut("");
      setGuests(1);
      setPin("");
      setReserveError("");
      setReserveSuccess("");
    }
  }, [selectedProperty]);

  const propertyTypes = [
    { id: "all", name: "Todos" },
    { id: "hotel", name: "Hotel" },
    { id: "hostel", name: "Hostel" },
    { id: "apartment", name: "Apartamento" },
    { id: "resort", name: "Resort" },
    { id: "villa", name: "Villa" },
    { id: "bnb", name: "Pousada" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/booking?page=${pageNum}&limit=12`;
      if (propertyType !== "all") url += `&propertyType=${propertyType}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching booking:", error);
      setLoading(false);
    }
  };

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 1;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diff = Math.ceil((end - start) / DAY_MS);
    return diff > 0 ? diff : 1;
  };

  const handleReserve = async () => {
    if (!selectedProperty) return;
    setReserveError("");
    setReserveSuccess("");

    if (!checkIn || !checkOut) {
      setReserveError("Selecione check-in e check-out");
      return;
    }

    const nights = Math.ceil((new Date(checkOut) - new Date(checkIn)) / DAY_MS);
    if (Number.isNaN(nights) || nights <= 0) {
      setReserveError("Check-out deve ser depois do check-in");
      return;
    }

    if (!/^\d{6}$/.test(pin)) {
      setReserveError("PIN da wallet inválido (6 dígitos)");
      return;
    }

    setReserveLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/booking/reservations`,
        {
          postId: selectedProperty._id,
          checkIn,
          checkOut,
          guests,
          pin,
          roomType: selectedProperty.bookingData?.propertyType,
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      if (data?.reservation) {
        setReserveSuccess("Reserva confirmada e paga via Wallet Socializa.");
      } else {
        setReserveSuccess("Reserva criada. Consulte suas reservas para detalhes.");
      }
    } catch (error) {
      setReserveError(error.response?.data?.message || "Não foi possível concluir a reserva");
    } finally {
      setReserveLoading(false);
    }
  };

  const handleCreatePropertyChange = (field, value) => {
    setCreateFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAmenity = () => {
    if (createFormData.amenityInput.trim()) {
      setCreateFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, prev.amenityInput.trim()],
        amenityInput: ""
      }));
    }
  };

  const handleRemoveAmenity = (index) => {
    setCreateFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const handleCreateProperty = async () => {
    setCreateError("");
    setCreateSuccess("");

    // Validações
    if (!createFormData.propertyName.trim()) {
      setCreateError("Nome da propriedade é obrigatório");
      return;
    }

    if (!createFormData.address.trim() || !createFormData.city.trim() || !createFormData.country.trim()) {
      setCreateError("Endereço, cidade e país são obrigatórios");
      return;
    }

    if (!createFormData.pricePerNight || createFormData.pricePerNight <= 0) {
      setCreateError("Preço por noite deve ser maior que 0");
      return;
    }

    setCreateLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/booking/posts`,
        {
          propertyName: createFormData.propertyName,
          propertyType: createFormData.propertyType,
          description: createFormData.description,
          address: createFormData.address,
          city: createFormData.city,
          country: createFormData.country,
          pricePerNight: parseFloat(createFormData.pricePerNight),
          amenities: createFormData.amenities,
          checkIn: createFormData.checkIn,
          checkOut: createFormData.checkOut,
          privacy: createFormData.privacy,
          images: [],
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      if (data?.post) {
        setPosts(prev => [data.post, ...prev]);
        setCreateSuccess("Hospedagem publicada com sucesso!");
        // Resetar formulário após 2 segundos
        setTimeout(() => {
          setShowCreateModal(false);
          setCreateFormData({
            propertyName: "",
            propertyType: "hotel",
            description: "",
            address: "",
            city: "",
            country: "",
            pricePerNight: "",
            amenities: [],
            amenityInput: "",
            checkIn: "14:00",
            checkOut: "12:00",
            privacy: "public",
          });
          setCreateSuccess("");
        }, 2000);
      }
    } catch (error) {
      setCreateError(error.response?.data?.error || "Erro ao publicar hospedagem");
    } finally {
      setCreateLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [propertyType]);

  if (loading && posts.length === 0) {
    return <div className="booking_feed_loading"><div className="booking_loader"></div><span>Carregando hospedagens...</span></div>;
  }

  return (
    <div className="booking_feed">
      <div className="booking_header">
        <div className="header_content">
          <div>
            <h1>Hospedagens</h1>
            <p>Encontre o lugar perfeito para ficar</p>
          </div>
          <button className="create_property_btn" onClick={() => setShowCreateModal(true)}>
            + Publicar Hospedagem
          </button>
        </div>
      </div>

      {propertyTypes.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {propertyTypes.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${propertyType === filter.id ? "active" : ""}`}
                onClick={() => setPropertyType(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="booking_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z"/>
          </svg>
          <h3>Nenhuma hospedagem encontrada</h3>
          <p>Seja o primeiro a cadastrar!</p>
        </div>
      ) : (
        <div className="booking_grid">
          {posts.map((post) => (
            <div key={post._id} className="booking_card" onClick={() => setSelectedProperty(post)}>
              <div className="booking_image">
                <img src={post.bookingData?.images?.[0] || post.images?.[0]?.url || post.images?.[0] || "/images/default-hotel.png"} alt="" />
                {post.bookingData?.rating && (
                  <span className="booking_rating">{post.bookingData.rating.toFixed(1)}</span>
                )}
              </div>
              <div className="booking_card_content">
                <span className="booking_type">{propertyTypes.find(t => t.id === post.bookingData?.propertyType)?.name || "Hotel"}</span>
                <h3>{post.bookingData?.propertyName || post.text}</h3>
                <p className="booking_location">{post.bookingData?.city}, {post.bookingData?.country || "Brasil"}</p>
                {post.bookingData?.amenities?.length > 0 && (
                  <div className="booking_amenities">
                    {post.bookingData.amenities.slice(0, 3).map((a, i) => (
                      <span key={i}>{a}</span>
                    ))}
                  </div>
                )}
                <div className="booking_footer">
                  <div className="booking_price">
                    <span className="booking_price_value">R$ {(post.bookingData?.pricePerNight?.amount || 0).toFixed(0)}</span>
                    <span className="booking_price_label">/noite</span>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <FeedPostMenu
                      post={post}
                      onEdit={handleEditPost}
                      onPostDeleted={handlePostDeleted}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="booking_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais"}
          </button>
        </div>
      )}

      {selectedProperty && (
        <div className="booking_modal_overlay" onClick={() => setSelectedProperty(null)}>
          <div className="booking_modal" onClick={(e) => e.stopPropagation()}>
            <button className="booking_modal_close" onClick={() => setSelectedProperty(null)}>✕</button>
            <div className="booking_modal_gallery">
              <img src={selectedProperty.bookingData?.images?.[0] || selectedProperty.images?.[0]} alt="" />
            </div>
            <div className="booking_modal_body">
              <h2>{selectedProperty.bookingData?.propertyName}</h2>
              <p className="booking_modal_location">{selectedProperty.bookingData?.address}, {selectedProperty.bookingData?.city}</p>
              <p className="booking_modal_desc">{selectedProperty.bookingData?.description || selectedProperty.text}</p>

              <div className="booking_modal_info">
                <div><strong>Check-in:</strong> {selectedProperty.bookingData?.checkIn || "14:00"}</div>
                <div><strong>Check-out:</strong> {selectedProperty.bookingData?.checkOut || "12:00"}</div>
              </div>

              {selectedProperty.bookingData?.amenities?.length > 0 && (
                <div className="booking_modal_amenities">
                  <h4>Comodidades</h4>
                  <div className="booking_amenities_list">
                    {selectedProperty.bookingData.amenities.map((a, i) => (
                      <span key={i}>{a}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="booking_reservation_panel">
                <div className="booking_price_breakdown">
                  <div className="booking_price_header">
                    <div>
                      <strong>Pagamento exclusivo</strong>
                      <p>Wallet Socializa (PIN necessário)</p>
                    </div>
                    <div className="booking_modal_price">
                      <span>R$ {(selectedProperty.bookingData?.pricePerNight?.amount || 0).toFixed(0)}</span>
                      <span>/noite</span>
                    </div>
                  </div>
                  <div className="booking_form_grid">
                    <label>
                      Check-in
                      <input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
                    </label>
                    <label>
                      Check-out
                      <input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
                    </label>
                    <label>
                      Hóspedes
                      <input
                        type="number"
                        min="1"
                        value={guests}
                        onChange={(e) => setGuests(Number(e.target.value) || 1)}
                      />
                    </label>
                    <label>
                      PIN da wallet (6 dígitos)
                      <input
                        type="password"
                        inputMode="numeric"
                        maxLength={6}
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
                      />
                    </label>
                  </div>

                  <div className="booking_totals">
                    <div>
                      <span>{calculateNights()} noite(s) x R$ {(selectedProperty.bookingData?.pricePerNight?.amount || 0).toFixed(0)}</span>
                      <strong>R$ {((selectedProperty.bookingData?.pricePerNight?.amount || 0) * calculateNights()).toFixed(2)}</strong>
                    </div>
                    <div>
                      <span>Taxas</span>
                      <strong>R$ 0,00</strong>
                    </div>
                    <div className="booking_total_row">
                      <span>Total</span>
                      <strong>R$ {((selectedProperty.bookingData?.pricePerNight?.amount || 0) * calculateNights()).toFixed(2)}</strong>
                    </div>
                  </div>

                  {reserveError && <div className="booking_feedback error">{reserveError}</div>}
                  {reserveSuccess && <div className="booking_feedback success">{reserveSuccess}</div>}

                  <div className="booking_modal_footer">
                    <button className="booking_reserve_btn" onClick={handleReserve} disabled={reserveLoading}>
                      {reserveLoading ? "Processando..." : "Reservar com Wallet"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {/* Create Hospedagem Modal */}
      {showCreateModal && (
        <div className="booking_modal_overlay">
          <div className="booking_create_modal">
            <div className="modal_header">
              <h2>Publicar Nova Hospedagem</h2>
              <button className="close_btn" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>

            <div className="create_form_container">
              {/* Property Name */}
              <div className="form_group">
                <label>Nome da Hospedagem *</label>
                <input
                  type="text"
                  placeholder="Ex: Casa com Piscina"
                  value={createFormData.propertyName}
                  onChange={(e) => handleCreatePropertyChange("propertyName", e.target.value)}
                  className={createError && !createFormData.propertyName.trim() ? "error" : ""}
                />
              </div>

              {/* Property Type */}
              <div className="form_group">
                <label>Tipo de Hospedagem *</label>
                <select
                  value={createFormData.propertyType}
                  onChange={(e) => handleCreatePropertyChange("propertyType", e.target.value)}
                >
                  <option value="hotel">Hotel</option>
                  <option value="hostel">Hostel</option>
                  <option value="apartment">Apartamento</option>
                  <option value="resort">Resort</option>
                  <option value="villa">Villa</option>
                  <option value="bnb">Bed & Breakfast</option>
                </select>
              </div>

              {/* Description */}
              <div className="form_group">
                <label>Descrição</label>
                <textarea
                  placeholder="Descreva sua hospedagem..."
                  value={createFormData.description}
                  onChange={(e) => handleCreatePropertyChange("description", e.target.value)}
                  rows={4}
                />
              </div>

              {/* Address */}
              <div className="form_group">
                <label>Endereço *</label>
                <input
                  type="text"
                  placeholder="Rua, número"
                  value={createFormData.address}
                  onChange={(e) => handleCreatePropertyChange("address", e.target.value)}
                  className={createError && !createFormData.address.trim() ? "error" : ""}
                />
              </div>

              {/* City and Country */}
              <div className="form_row">
                <div className="form_group">
                  <label>Cidade *</label>
                  <input
                    type="text"
                    placeholder="Cidade"
                    value={createFormData.city}
                    onChange={(e) => handleCreatePropertyChange("city", e.target.value)}
                    className={createError && !createFormData.city.trim() ? "error" : ""}
                  />
                </div>
                <div className="form_group">
                  <label>País *</label>
                  <input
                    type="text"
                    placeholder="País"
                    value={createFormData.country}
                    onChange={(e) => handleCreatePropertyChange("country", e.target.value)}
                    className={createError && !createFormData.country.trim() ? "error" : ""}
                  />
                </div>
              </div>

              {/* Price Per Night */}
              <div className="form_group">
                <label>Preço por Noite (R$) *</label>
                <input
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  value={createFormData.pricePerNight}
                  onChange={(e) => handleCreatePropertyChange("pricePerNight", e.target.value)}
                  className={createError && (!createFormData.pricePerNight || parseFloat(createFormData.pricePerNight) <= 0) ? "error" : ""}
                />
              </div>

              {/* Amenities */}
              <div className="form_group">
                <label>Comodidades</label>
                <div className="amenity_input_group">
                  <input
                    type="text"
                    placeholder="Ex: WiFi, Piscina, Academia"
                    value={createFormData.amenityInput}
                    onChange={(e) => handleCreatePropertyChange("amenityInput", e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddAmenity()}
                  />
                  <button type="button" onClick={handleAddAmenity} className="add_amenity_btn">
                    Adicionar
                  </button>
                </div>
                {createFormData.amenities.length > 0 && (
                  <div className="amenities_list">
                    {createFormData.amenities.map((amenity, index) => (
                      <div key={index} className="amenity_tag">
                        <span>{amenity}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAmenity(index)}
                          className="remove_amenity"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Check-in and Check-out */}
              <div className="form_row">
                <div className="form_group">
                  <label>Check-in</label>
                  <input
                    type="time"
                    value={createFormData.checkIn}
                    onChange={(e) => handleCreatePropertyChange("checkIn", e.target.value)}
                  />
                </div>
                <div className="form_group">
                  <label>Check-out</label>
                  <input
                    type="time"
                    value={createFormData.checkOut}
                    onChange={(e) => handleCreatePropertyChange("checkOut", e.target.value)}
                  />
                </div>
              </div>

              {/* Privacy */}
              <div className="form_group">
                <label>Privacidade</label>
                <select
                  value={createFormData.privacy}
                  onChange={(e) => handleCreatePropertyChange("privacy", e.target.value)}
                >
                  <option value="public">Pública</option>
                  <option value="friends">Apenas Amigos</option>
                </select>
              </div>

              {/* Error Message */}
              {createError && <div className="error_message">{createError}</div>}

              {/* Success Message */}
              {createSuccess && <div className="success_message">{createSuccess}</div>}

              {/* Buttons */}
              <div className="form_buttons">
                <button
                  onClick={handleCreateProperty}
                  disabled={createLoading}
                  className={`create_btn ${createLoading ? "loading" : ""}`}
                >
                  {createLoading ? "Publicando..." : "Publicar Hospedagem"}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  disabled={createLoading}
                  className="cancel_btn"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
